import {
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { ADMIN_SHELL_ICON_BUTTON_CLASS } from './adminShellChrome.styles.js';

export type OverflowMenuItem = {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /** Optional stable test id; defaults to `${menuTestId}-item-${id}`. */
  readonly testId?: string;
};

export interface OverflowMenuProps {
  /** Accessible name for the icon-only trigger (Czech-ready via caller). */
  readonly ariaLabel: string;
  readonly items: readonly OverflowMenuItem[];
  readonly testId?: string;
  readonly className?: string;
}

const POPOVER_OFFSET_PX = 4;
const VIEWPORT_EDGE_PAD_PX = 8;
const MENU_MIN_WIDTH_PX = 176; // 11rem

type MenuPosition = {
  readonly top: number;
  readonly left: number;
  readonly minWidth: number;
  /** Viewport band: innerWidth − 2×VIEWPORT_EDGE_PAD_PX (AC-07 / G9). */
  readonly maxWidth: number;
};

/**
 * Shared overflow menu for secondary header/toolbar actions (Retail V1 Part 6).
 * Distinct from list-kit OverflowActionsMenu (row More).
 * Labels are caller-supplied — no hardcoded copy.
 *
 * Open panel portals to document.body with fixed positioning so toolbar
 * overflow/stacking contexts cannot clip or tint the popover incorrectly.
 */
export const OverflowMenu = memo(
  ({
    ariaLabel,
    items,
    testId = 'overflow-menu',
    className,
  }: OverflowMenuProps): JSX.Element | null => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<MenuPosition | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const menuId = useId();

    const closeMenu = useCallback((restoreFocus: boolean): void => {
      setOpen(false);
      setPosition(null);
      if (restoreFocus) {
        triggerRef.current?.focus();
      }
    }, []);

    const computePosition = useCallback((): void => {
      const trigger = triggerRef.current;
      if (trigger == null) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const menuEl = menuRef.current;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      // Cap to the same horizontal band used by left clamp — otherwise left pins
      // to pad while an over-wide panel clips under overflow-hidden (AC-07 / G9).
      const maxWidth = Math.max(0, viewportWidth - 2 * VIEWPORT_EDGE_PAD_PX);
      const measuredWidth = Math.max(menuEl?.offsetWidth ?? 0, MENU_MIN_WIDTH_PX);
      const menuWidth = Math.min(measuredWidth, maxWidth);
      const menuHeight = menuEl?.offsetHeight ?? 0;
      const minWidth = Math.min(MENU_MIN_WIDTH_PX, maxWidth);

      // Prefer right-align to trigger (mobile toolbar overflow).
      let left = rect.right - menuWidth;
      left = Math.max(
        VIEWPORT_EDGE_PAD_PX,
        Math.min(left, viewportWidth - menuWidth - VIEWPORT_EDGE_PAD_PX),
      );

      let top = rect.bottom + POPOVER_OFFSET_PX;
      if (menuHeight > 0 && top + menuHeight > viewportHeight - VIEWPORT_EDGE_PAD_PX) {
        const above = rect.top - POPOVER_OFFSET_PX - menuHeight;
        if (above >= VIEWPORT_EDGE_PAD_PX) {
          top = above;
        } else {
          top = Math.max(
            VIEWPORT_EDGE_PAD_PX,
            viewportHeight - menuHeight - VIEWPORT_EDGE_PAD_PX,
          );
        }
      }

      setPosition({
        top,
        left,
        minWidth,
        maxWidth,
      });
    }, []);

    useLayoutEffect(() => {
      if (!open) {
        setPosition(null);
        return undefined;
      }
      // Seed from trigger so the portal can mount, then refine with measured size.
      computePosition();
      const rafId = window.requestAnimationFrame(() => {
        computePosition();
      });
      return (): void => {
        window.cancelAnimationFrame(rafId);
      };
    }, [open, computePosition, items]);

    // Portal puts the panel at document.body — without moving focus, Tab leaves the
    // toolbar and never reaches menuitems (absolute-in-root used to keep them next).
    useLayoutEffect(() => {
      if (!open || position == null) {
        return;
      }
      const firstEnabled = menuRef.current?.querySelector<HTMLButtonElement>(
        'button[role="menuitem"]:not(:disabled)',
      );
      firstEnabled?.focus();
    }, [open, position, items]);

    useEffect(() => {
      if (!open) {
        return undefined;
      }

      // Hotfix-safe: close on scroll/resize/orientation so the portal never
      // drifts under sticky chrome or leaves the viewport.
      const closeFromViewportChange = (): void => {
        closeMenu(false);
      };

      const onPointerDown = (event: PointerEvent): void => {
        const target = event.target as Node;
        if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
          return;
        }
        closeMenu(false);
      };

      const onKeyDown = (event: globalThis.KeyboardEvent): void => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu(true);
        }
      };

      document.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
      window.addEventListener('resize', closeFromViewportChange);
      window.addEventListener('orientationchange', closeFromViewportChange);
      window.addEventListener('scroll', closeFromViewportChange, true);

      return (): void => {
        document.removeEventListener('pointerdown', onPointerDown);
        document.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('resize', closeFromViewportChange);
        window.removeEventListener('orientationchange', closeFromViewportChange);
        window.removeEventListener('scroll', closeFromViewportChange, true);
      };
    }, [open, closeMenu]);

    const toggle = useCallback((): void => {
      setOpen((prev) => !prev);
    }, []);

    const onMenuKeyDown = useCallback(
      (event: KeyboardEvent<HTMLUListElement>): void => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeMenu(true);
        }
      },
      [closeMenu],
    );

    if (items.length === 0) {
      return null;
    }

    const rootClass =
      className !== undefined && className.length > 0
        ? `relative inline-flex shrink-0 ${className}`
        : 'relative inline-flex shrink-0';

    const menuStyle: CSSProperties | undefined =
      position != null
        ? {
            top: position.top,
            left: position.left,
            minWidth: position.minWidth,
            maxWidth: position.maxWidth,
            // Inline opaque surface — do not rely solely on utility cascade for AC-01.
            backgroundColor: 'var(--color-an-surface, #ffffff)',
            opacity: 1,
            isolation: 'isolate',
            pointerEvents: 'auto',
          }
        : undefined;

    const menu =
      open && position != null
        ? createPortal(
            <ul
              ref={menuRef}
              id={menuId}
              role="menu"
              aria-label={ariaLabel}
              data-testid={`${testId}-menu`}
              className="fixed z-[var(--z-popover)] overflow-hidden rounded-lg border border-[var(--color-an-border)] bg-[var(--color-an-surface,#ffffff)] py-1 shadow-[var(--elevation-2)]"
              style={menuStyle}
              onKeyDown={onMenuKeyDown}
            >
              {items.map((item) => {
                const isDisabled = item.disabled === true;
                return (
                  <li key={item.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      disabled={isDisabled}
                      aria-disabled={isDisabled || undefined}
                      data-testid={item.testId ?? `${testId}-item-${item.id}`}
                      className={[
                        'flex min-h-11 w-full items-center px-3 py-2.5 text-left text-sm',
                        'text-[var(--color-an-text)]',
                        'hover:bg-[var(--color-an-primary-soft)]',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]',
                        'focus-visible:outline-[var(--color-an-focus-ring)]',
                        'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-transparent',
                      ].join(' ')}
                      onClick={() => {
                        if (isDisabled) {
                          return;
                        }
                        setOpen(false);
                        setPosition(null);
                        item.onSelect();
                      }}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body,
          )
        : null;

    return (
      <div
        ref={rootRef}
        className={rootClass}
        data-testid={testId}
        role="presentation"
      >
        <button
          ref={triggerRef}
          type="button"
          className={ADMIN_SHELL_ICON_BUTTON_CLASS}
          aria-label={ariaLabel}
          title={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          data-testid={`${testId}-trigger`}
          onClick={toggle}
        >
          <MoreVertical className="h-5 w-5" aria-hidden />
        </button>
        {menu}
      </div>
    );
  },
);

OverflowMenu.displayName = 'OverflowMenu';
